import axios from "axios";

/* ================================
   BASE CONFIG
================================ */
const apiUrl = "https://v3api.digidoc.co.in/v1";

// const apiUrl = "http://localhost:8888/v1/";

const API = axios.create({
  baseURL: apiUrl,
  // headers: {
  //   "Content-Type": "application/json",
  // },
});

/* ================================
   REFRESH CONTROL
================================ */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

/* ================================
   REQUEST INTERCEPTOR
================================ */
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/* ================================
   RESPONSE INTERCEPTOR
================================ */
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ❌ Do NOT retry refresh API itself
    if (originalRequest?.url?.includes("/refresh-token")) {
      return Promise.reject(error);
    }

    // 🔐 Only retry if token expired (backend must return this message)
    const isTokenExpired =
      error.response?.status === 401 &&
      error.response?.data?.message === "TOKEN_EXPIRED";

    if (!isTokenExpired || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return API(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token");

      const res = await API.post("/auth/doctor/refresh-token", {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token } = res.data.response || {};

      if (!access_token || !refresh_token) {
        throw new Error("Invalid refresh token response");
      }

      // ✅ Save new tokens
      localStorage.setItem("token", access_token);
      localStorage.setItem("refreshToken", refresh_token);

      API.defaults.headers.common.Authorization = `Bearer ${access_token}`;

      processQueue(null, access_token);

      // Retry original request
      return API(originalRequest);
    } catch (err) {
      processQueue(err, null);

      // 🚪 Logout ONLY when refresh fails
      localStorage.clear();
      sessionStorage.clear();
      window.location.replace("/");

      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  },
);

/* ================================
   REUSABLE API HELPERS
================================ */
export const GetAPI = async (url, callback) => {
  try {
    const res = await API.get(url);
    callback(res.data);
  } catch (err) {
    callback(err?.response?.data || { message: "Network error" });
  }
};

export const PostAPI = async ({ url, data }, callback) => {
  try {
    const res = await API.post(url, data);
    callback(res.data);
  } catch (err) {
    callback(err?.response?.data || { message: "Network error" });
  }
};

export const PutAPI = async ({ url, data }, callback) => {
  try {
    const res = await API.put(url, data);
    callback(res.data);
  } catch (err) {
    callback(err?.response?.data || { message: "Network error" });
  }
};

export const PatchAPI = async ({ url, data }, callback) => {
  try {
    const res = await API.patch(url, data);
    callback(res.data);
  } catch (err) {
    callback(err?.response?.data || { message: "Network error" });
  }
};

export const DeleteAPI = async ({ url, data }, callback) => {
  try {
    const res = await API.delete(url, { data });
    callback(res.data);
  } catch (err) {
    callback(err?.response?.data || { message: "Network error" });
  }
};

export default API;
